import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { z } from 'zod'
import { buildPostSigninRedirectOptions } from '@server/post-signin'
import { migrateGuestHistoryForRequest } from './user.rpc'
import { enqueueServerAnalyticsEvent } from '@server/analytics'
import { AUTH_METHODS } from '@shared/auth-analytics'

const loadAuth = async () => (await import('@server/auth')).auth

export const completePostSignin = createServerFn({ method: 'GET' })
  .inputValidator(
    z.object({
      redirectPath: z.string().optional(),
      authMethod: z.enum(AUTH_METHODS).optional(),
    }),
  )
  .handler(async ({ data }) => {
    const request = getRequest()
    const auth = await loadAuth()
    const session = await auth.api.getSession({ headers: request.headers })
    const sessionData = session as {
      user?: { id?: string; billingPlan?: string | null }
      session?: { id?: string }
    }
    const userId = sessionData.user?.id
    let guestHistoryMigration = { migrated: false, count: 0 }

    if (userId) {
      try {
        const migrationResult = (await migrateGuestHistoryForRequest(
          request.headers,
          userId,
        )) as typeof guestHistoryMigration | undefined
        if (migrationResult) guestHistoryMigration = migrationResult
      } catch (error) {
        console.error(
          '[Auth] Failed to migrate guest history after sign-in:',
          error,
        )
      }

      const sessionId = sessionData.session?.id
      if (sessionId) {
        try {
          await enqueueServerAnalyticsEvent({
            eventName: 'sign_in_completed',
            idempotencyKey: `sign-in:${sessionId}`,
            userId,
            properties: {
              plan_id: sessionData.user?.billingPlan ?? 'unknown',
              auth_method: data.authMethod ?? 'unknown',
              guest_history_migrated: guestHistoryMigration.migrated,
              migrated_conversation_count: guestHistoryMigration.count,
            },
          })
        } catch (error) {
          console.error(
            '[Analytics] Failed to capture completed sign-in:',
            error,
          )
        }
      }
    }

    const isSecureCookie =
      new URL(request.url).protocol === 'https:' ||
      process.env.NODE_ENV === 'production'

    return buildPostSigninRedirectOptions({
      userId,
      redirectPath: data.redirectPath,
      isSecureCookie,
    })
  })
